// /**
//  * returned List
//  */

// React
import React, { useState } from 'react';

// UI
import {
  createStyles,
  lighten,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Select,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
} from '@material-ui/core';
import jMoment from 'moment-jalaali';
import { translate, checkMatchMedia } from '../../../utils/helpers';
// Type
import { SkuInventory } from '../../warehouses/interfaces';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiFileDocumentOutline,
  mdiClose,
  mdiBriefcaseDownloadOutline,
} from '@mdi/js';
import theme from '../../../theme';
import clsx from 'clsx';
import Can from '../../../components/Can';

enum Type {
  skus = 'skus',
  warehouse = 'warehouse',
}

type Data = SkuInventory;
type Props = {
  data: any;
  type: Type;
  handleNumSelect: (selected: any[], selectedLength: number) => void;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      whiteSpace: 'nowrap',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      justifyContent: 'space-between',
    },
    highlight:
      theme.palette.type === 'light'
        ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
    title: {
      // flex: '1 1 100%',
    },
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-body': {
        whiteSpace: 'nowrap',
      },
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells = [
  {
    id: 'customerName',
    label: 'نام سفارش‌دهنده',
  },
  { id: 'id', label: 'شناسه سفارش' },
  { id: 'trackingCode', label: 'کد پیگیری' },
  { id: 'deliveryType', label: 'نوع ارسال' },
  {
    id: 'deliveryDate',
    label: 'زمان تحویل',
  },
  { id: 'status', label: 'وضعیت سفارش' },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {
    order,
    orderBy,
    onRequestSort,
    numSelected,
    rowCount,
    onSelectAllClick,
  } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
            style={{ color: '#fff' }}
          />
        </StyledTableCell>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={1}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  setOpenChangeStatus: any;
  selected: any;
  data: any;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  return <></>;
};

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('amount');
  const [dense] = React.useState(false);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [openChangeStatus, setOpenChangeStatus] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = props.data.map((n: any) => n.id);
      setSelected(newSelecteds);
      props.handleNumSelect(newSelecteds, newSelecteds.length);
      return;
    }
    setSelected([]);
    props.handleNumSelect([], 0);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    id: string,
    row: any
  ) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
      props.handleNumSelect(newSelected, newSelected.length, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
      props.handleNumSelect(newSelected, newSelected.length, id);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
      props.handleNumSelect(newSelected, newSelected.length, id);
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
      props.handleNumSelect(
        (newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
          id
        )),
        newSelected.length
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id: string) => props.selected.indexOf(id) !== -1;

  return (
    <div className={classes.root}>
      <EnhancedTableToolbar
        numSelected={selected.length}
        setOpenChangeStatus={setOpenChangeStatus}
        selected={selected}
        data={props.data}
      />
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            numSelected={selected.length}
            onSelectAllClick={handleSelectAllClick}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                const isItemSelected = isSelected(row.id);

                return (
                  <TableRow
                    key={index}
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    selected={isItemSelected}
                  >
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        key={index}
                        onClick={event => handleClick(event, row.id)}
                        checked={isSelected(row.id)}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </StyledTableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.customerName}
                    </TableCell>
                    <TableCell align="center">{row.id}</TableCell>
                    <TableCell align="center">{row.trackingCode}</TableCell>
                    <TableCell align="center">
                      {' '}
                      {translate(row.deliveryType)}
                    </TableCell>
                    <TableCell align="center">
                      {' '}
                      <span>
                        {jMoment(row.deliveryDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                      </span>
                      &nbsp; &nbsp;- &nbsp; &nbsp;
                      <span>
                        {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                          .locale('fa')
                          .format('HH')}
                        `}
                        {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                          .locale('fa')
                          .format('HH')}`}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <span>{translate(row.status, 'order')}</span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <div>
                        <Link to={`/orders-resend/${row.id}`}>
                          <IconButton
                            size="small"
                            title="تعیین وضعیت سفارش"
                            data-cy="edit"
                          >
                            <Icon
                              color={theme.palette.blue.main}
                              path={mdiBriefcaseDownloadOutline}
                              size={1}
                            />
                          </IconButton>
                        </Link>
                        <Link to={`/orders/${row.id}`}>
                          <IconButton
                            aria-label="details"
                            size="small"
                            title="جزئیات سفارش"
                            data-cy="details"
                          >
                            <Icon
                              path={mdiFileDocumentOutline}
                              size={1}
                              color={theme.palette.green.main}
                            />
                          </IconButton>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
